/**
 * Shared Orders Board contracts. Consumed by `apps/admin-api` (response shape) and
 * `apps/admin-web` (HTTP client + view model). Bucket label and intra-column sort are
 * always computed server-side; this module only carries the wire shape.
 */

import type { OrderCommSnapshotDto } from "../orders-comm/order-communication.types";
import type { DunningStatus } from "../payments/dunning.types";

export type BoardLabel = "critical" | "rush" | "standard";

/**
 * The order's paid/unpaid disposition on the board. `'LinkSent'` (additive)
 * marks an order with a live, un-revoked payment link awaiting customer
 * action — the payment-mode board's "Link Sent" lane. Existing
 * `'Paid' | 'Partial' | 'Unpaid'` consumers are unaffected.
 */
export type BoardPaymentStatus = "Paid" | "Partial" | "Unpaid" | "LinkSent";

/**
 * The board's two view modes. `'urgency'` is the existing SLA-deadline board
 * and the default (preserves today's behaviour); `'payment'` is the dunning
 * supervision board (slices 10–15). The `as const` object is the single source
 * of truth for the value set (`.claude/rules/no-magic-strings.md` /
 * `typescript-domain-enums.md`) — the controller's view-mode branch (slice 11)
 * and the FE store default (slice 13) reference its members rather than
 * re-spelling the literals.
 */
export const BOARD_VIEW_MODE = {
  Urgency: "urgency",
  Payment: "payment",
} as const;

export type BoardViewMode =
  (typeof BOARD_VIEW_MODE)[keyof typeof BOARD_VIEW_MODE];

/**
 * The five payment-board lanes, in display order. Mirrors the per-lane keys on
 * {@link PaymentBoardResponse} / {@link PaymentBoardCounts}.
 */
export type PaymentStage =
  | "needsAttention"
  | "unpaid"
  | "linkSent"
  | "partial"
  | "paid";

/**
 * The six dunning lifecycle states. Re-exported from `../payments/dunning.types`
 * (the `DUNNING_STATUS` const is the single source of truth for the closed set —
 * `.claude/rules/no-magic-strings.md`) so payment-board consumers can import it
 * alongside the rest of the board contract from this one module.
 */
export type { DunningStatus };

/**
 * Re-exported from `../orders-comm/order-communication.types` (the
 * `ORDER_COMM_STATE` const is the single source of truth for the closed set)
 * so board consumers can import the card's comm snapshot type alongside the
 * rest of the board contract from this one module.
 */
export type { OrderCommSnapshotDto };

export interface BoardCardDto {
  orderId: number;
  orderNumber: string;
  companyName: string;
  propertyAddress: string;
  /**
   * Two-letter state abbreviation resolved from `[Location].[States].Abbr` via
   * the order's `StateId`. `null` when the order has no state.
   */
  stateAbbr: string | null;
  /**
   * County name resolved from `[Location].[Counties].Name` via the order's
   * `CountyId`. `null` when the order has no county.
   */
  county: string | null;
  /** The order's `[Order].[Orders].City` free-text value, or `null`. */
  city: string | null;
  orderStatusId: number;
  orderStatus: string;
  paymentStatus: BoardPaymentStatus;
  totalAmount: number;
  /**
   * ISO 8601 string of the raw `[Order].[Orders].DueDateUtc` (pre-pause).
   * `null` when the order has no SLA deadline (no SLA-driver service, missing
   * business hours, or completed) — such rows still appear on the board.
   */
  dueDateUtc: string | null;
  /** `true` when the order is currently paused (`[Order].[Orders].IsPaused`). */
  isPaused: boolean;
  /**
   * The order's owner id — the boundary alias for `[Order].[Orders].OwnedByUserId`
   * (a JurisTech `[User].[UserProfile].Id`). `null` when the order is unassigned.
   * The avatar hue/initials are derived from this id + {@link ownerName}
   * client-side; the field carries no server-rendered avatar.
   */
  assignedToUserId: number | null;
  /**
   * The owner's composed display name (`FirstName LastName`), resolved
   * application-side from JurisTech via `OrderOwnerResolver` (no cross-DB JOIN).
   * `null` only when the order has no owner. A since-deactivated owner still
   * resolves to its plain name (no "Deactivated"/"Unknown user" treatment).
   */
  ownerName: string | null;
  /**
   * ISO 8601 string of the pause-aware SLA deadline:
   * `DueDateUtc + PauseDurationMs (+ live pause segment while currently paused)`.
   * Computed app-side with the injected `ClockService` at read time. While an
   * order is paused this timestamp marches with `now`, but
   * `(effectiveDeadlineUtc − now)` stays constant — "time remaining" is frozen.
   * `null` when `dueDateUtc` is `null` (no deadline to shift); such rows are
   * never `critical` and sort after all dated rows within their bucket.
   */
  effectiveDeadlineUtc: string | null;
  /**
   * ISO 8601 string of `[Order].[Orders].CreatedDate`. Drives order-age display
   * in payment mode. `null` when `CreatedDate` is `NULL` (such rows fall outside
   * the dunning launch cutoff but still render on the board).
   */
  createdDateUtc: string | null;
  /**
   * ISO 8601 string of the most-recent un-revoked payment-link send (MAX
   * `[Order].[PaymentLinkTokens].CreatedUtc`). `null` when no link has been
   * sent. Basis for link-age in payment mode.
   */
  paymentLinkSentUtc: string | null;
  /**
   * The order's dunning state from `[Order].[PaymentDunning].Status`, or `null`
   * when no dunning row exists yet (the card then buckets purely on allocation
   * state — see slice 10 rollup).
   */
  dunningStatus: DunningStatus | null;
  /**
   * `[Order].[PaymentDunning].RemindersSent` — the touch counter (`N/5`).
   * `null` when no dunning row exists.
   */
  remindersSent: number | null;
  /**
   * ISO 8601 string of `[Order].[PaymentDunning].NextReminderUtc` — drives the
   * next-chase ("in Xd"/"overdue") affordance. `null` when no row, or when the
   * row is terminal/parked (`stalled`/`no_email`/`paused`/`abandoned`/`paid`).
   */
  nextReminderUtc: string | null;
  /**
   * Per-order communication snapshot (state + counts + queue membership),
   * derived at read time from external `[Thread].[Messages]` rows. Server
   * always populates it; typed nullable so a FE deployed ahead of the BE
   * renders nothing instead of `undefined`
   * (docs/plans/order-communication-state/PLAN-FINAL.md).
   */
  comm: OrderCommSnapshotDto | null;
}

export interface BoardCounts {
  critical: number;
  rush: number;
  standard: number;
}

export interface BoardResponse {
  critical: BoardCardDto[];
  rush: BoardCardDto[];
  standard: BoardCardDto[];
  counts: BoardCounts;
}

/** Per-lane card counts for the payment-mode board (one number per stage). */
export interface PaymentBoardCounts {
  needsAttention: number;
  unpaid: number;
  linkSent: number;
  partial: number;
  paid: number;
}

/**
 * The payment-mode board response — the 5-lane sibling of {@link BoardResponse}.
 * Each lane is the `bucketForPaymentStage()` rollup of cards (slice 10); the
 * `abandoned`/`no_email` dunning states stay visible in `needsAttention`.
 */
export interface PaymentBoardResponse {
  needsAttention: BoardCardDto[];
  unpaid: BoardCardDto[];
  linkSent: BoardCardDto[];
  partial: BoardCardDto[];
  paid: BoardCardDto[];
  counts: PaymentBoardCounts;
}

/**
 * A user an order may be assigned to — the wire shape of
 * `GET /orders/assignable-users` (R4). `userId` is a JurisTech
 * `[User].[UserProfile].Id`; `name` is the composed `FirstName LastName`.
 * Scoped server-side to active internal admins/super-admins. Backs the board's
 * assign popover (slice 03) and the "Owned by" filter dropdown (slice 04).
 */
export interface AssignableUser {
  userId: number;
  name: string;
}

/**
 * The wire shape of `GET /orders/owned-count` (R4 slice 04) — the number of
 * orders owned by a given user id. Independent of the board's `TOP` cap and the
 * board's other active filters so the "My orders" pill count is accurate.
 */
export interface OwnedCountResponse {
  count: number;
}

/**
 * A dashboard status an operator may pick from the status dropdown — the wire
 * shape of `GET /orders/statuses` (R3 slice 02). Resolved from
 * `[Dashboard].[Statuses]` with `IsVisible = 1` via the `OrderStatusReader`
 * seam, so the completed status (`35`, `IsVisible = 0`) is never present.
 * Matches `StatusStat` minus the per-status order count.
 */
export interface SelectableStatus {
  statusId: number;
  displayName: string;
}
